import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Loader2, Package, GraduationCap, Download, PlayCircle, FileText, ExternalLink, LogIn, PackageOpen, Store, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { MKT_GOLD } from "../../lib/marketplace";
import { apiMisCompras, apiDescargarArchivo, apiSolicitarReembolso } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const soles = (n) => (Number(n || 0) > 0 ? `S/ ${Number(n).toFixed(2)}` : "Gratis");

export default function MisComprasPage() {
  const { user } = useAuth();
  const [data, setData] = useState(undefined);
  const [bajando, setBajando] = useState(null);
  const [refundFor, setRefundFor] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  const cargar = () => apiMisCompras().then(setData).catch(() => setData(null));

  useEffect(() => {
    if (user && user !== false) cargar();
    else if (user === false) setData(null);
  }, [user]);

  const descargar = async (fileId) => {
    setBajando(fileId);
    try {
      const { url } = await apiDescargarArchivo(fileId);
      window.open(url, "_blank", "noopener");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setBajando(null); }
  };

  const enviarReembolso = async () => {
    if (motivo.trim().length < 5) { toast.error("Cuéntanos brevemente el motivo (mínimo 5 caracteres)."); return; }
    setEnviando(true);
    try {
      await apiSolicitarReembolso(refundFor.tipo, refundFor.item_id, motivo.trim());
      toast.success("Solicitud de reembolso enviada. El equipo de ConstruLink la revisará.");
      setRefundFor(null); setMotivo("");
      await cargar();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setEnviando(false); }
  };

  if (user === false) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-4" data-testid="compras-login">
        <ShoppingBag size={40} className="mx-auto" style={{ color: MKT_GOLD }} />
        <h1 className="mt-4 text-xl font-extrabold text-slate-900">Tus compras</h1>
        <p className="mt-2 text-slate-500">Inicia sesión para ver los recursos que has adquirido.</p>
        <Link to="/login" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold" style={{ backgroundColor: MKT_GOLD }}>
          <LogIn size={16} /> Iniciar sesión
        </Link>
      </div>
    );
  }

  if (data === undefined) return <div className="flex items-center justify-center py-24 text-slate-500" data-testid="compras-loading"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;

  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mis-compras-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 inline-flex items-center gap-2">
          <ShoppingBag size={26} style={{ color: MKT_GOLD }} /> Mis Compras
        </h1>
        <p className="mt-1 text-slate-500 text-sm">Recursos digitales y cursos que has adquirido en el Marketplace.</p>

        {items.length === 0 ? (
          <div className="mt-8 text-center py-16 rounded-2xl border border-dashed border-slate-300 bg-white" data-testid="compras-vacio">
            <PackageOpen size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">Aún no tienes compras.</p>
            <Link to="/tienda" className="mt-4 inline-block font-bold" style={{ color: MKT_GOLD }}>Explorar el Marketplace</Link>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="compras-items">
            {items.map((it) => (
              <div key={`${it.tipo}-${it.item_id}`} className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5" data-testid={`compra-${it.item_id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
                    {it.cover ? <img src={it.cover} alt={it.titulo} className="w-full h-full object-cover" /> : (it.tipo === "curso" ? <GraduationCap size={22} style={{ color: MKT_GOLD }} /> : <Package size={22} style={{ color: MKT_GOLD }} />)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: MKT_GOLD }}>{it.tipo === "curso" ? "Curso" : "Producto"}</span>
                    <h3 className="font-bold text-slate-900 truncate">{it.titulo}</h3>
                    <p className="text-xs text-slate-500 inline-flex items-center gap-1 truncate"><Store size={12} /> {it.vendedor || it.tienda} · {soles(it.precio_pagado)}</p>
                  </div>
                </div>

                {/* Entrega protegida */}
                {it.tipo === "producto" && it.archivo && (
                  <div className="mt-3 pt-3 border-t border-slate-50">
                    <button onClick={() => descargar(it.archivo)} disabled={bajando === it.archivo} data-testid={`compra-descargar-${it.item_id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold text-sm hover:brightness-110 disabled:opacity-60" style={{ backgroundColor: MKT_GOLD }}>
                      {bajando === it.archivo ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Descargar {it.archivoNombre ? `· ${it.archivoNombre}` : ""}
                    </button>
                  </div>
                )}

                {it.tipo === "curso" && (
                  <div className="mt-3 pt-3 border-t border-slate-50 space-y-3">
                    {it.videos?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-slate-500 mb-1.5">Videos (visualización)</p>
                        <ul className="space-y-1">
                          {it.videos.map((v, i) => (
                            <li key={i}>
                              <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-700 inline-flex items-center gap-1.5 hover:underline" data-testid={`compra-video-${it.item_id}-${i}`}>
                                <PlayCircle size={15} style={{ color: MKT_GOLD }} /> {v.titulo || "Lección"} <ExternalLink size={12} className="text-slate-400" />
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {it.documentos?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-slate-500 mb-1.5">Materiales descargables</p>
                        <ul className="space-y-1">
                          {it.documentos.map((d, i) => (
                            <li key={i}>
                              <button onClick={() => descargar(d.archivo)} disabled={bajando === d.archivo} data-testid={`compra-doc-${it.item_id}-${i}`} className="text-sm text-slate-700 inline-flex items-center gap-1.5 hover:underline disabled:opacity-60">
                                {bajando === d.archivo ? <Loader2 size={14} className="animate-spin" /> : <FileText size={15} style={{ color: MKT_GOLD }} />} {d.titulo || "Documento"}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(!it.videos?.length && !it.documentos?.length) && <p className="text-xs text-slate-400">Este curso no tiene materiales descargables.</p>}
                  </div>
                )}

                {/* Reembolso / incidencia */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                  {it.descargado && <span className="text-[11px] font-semibold text-slate-500 inline-flex items-center gap-1"><Download size={12} /> Material descargado · reembolso no disponible</span>}
                  {it.reembolso?.estado === "solicitado" && (
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`compra-reembolso-estado-${it.item_id}`}>Reembolso en revisión</span>
                  )}
                  {it.reembolso?.estado === "aprobado" && (
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`compra-reembolso-estado-${it.item_id}`}>Reembolso aprobado</span>
                  )}
                  {it.reembolso?.estado === "rechazado" && (
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200" title={it.reembolso?.comentario_admin || ""} data-testid={`compra-reembolso-estado-${it.item_id}`}>Reembolso rechazado</span>
                  )}
                  {it.reembolsable && (
                    <button onClick={() => { setRefundFor(it); setMotivo(""); }} data-testid={`compra-solicitar-reembolso-${it.item_id}`} className="ml-auto text-xs font-semibold text-slate-600 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50">
                      <RotateCcw size={13} /> Solicitar reembolso
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {refundFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="reembolso-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
            <button onClick={() => setRefundFor(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" data-testid="reembolso-cerrar"><X size={20} /></button>
            <h3 className="text-lg font-extrabold text-slate-900 inline-flex items-center gap-2"><RotateCcw size={18} style={{ color: MKT_GOLD }} /> Solicitar reembolso</h3>
            <p className="mt-1 text-sm text-slate-500 truncate">{refundFor.titulo}</p>
            <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              El reembolso solo procede si <b>aún no has descargado</b> el material y estás dentro de los <b>3 días</b> de la compra. La devolución del dinero se realiza en un máximo de 30 días.
            </div>
            <label className="block mt-4 text-sm font-semibold text-slate-700">Motivo del reembolso</label>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={4} maxLength={1000} data-testid="reembolso-motivo"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
              placeholder="Cuéntanos por qué solicitas el reembolso…" />
            <p className="mt-1 text-[11px] text-slate-400">Plazo para solicitar: 3 días desde la compra. La devolución del dinero se realiza en un máximo de 30 días.</p>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setRefundFor(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={enviarReembolso} disabled={enviando} data-testid="reembolso-enviar" className="px-5 py-2 rounded-lg text-white font-bold text-sm hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-2" style={{ backgroundColor: MKT_GOLD }}>
                {enviando && <Loader2 size={15} className="animate-spin" />} Enviar solicitud
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
